import {
    CcRequirement,
    CcRequirementParentChild,
    CcRequirementVersion,
    CcRequirementRevision,
    CcRequirementState,
    CcRequirementHistory,
    CcRequirementTempAnswer,
    CcRequirementTempAnswerVersion,
    Lifecycle,
    LifecyclePhase,
} from "server/models"
import { OrderType, StateType } from "client/utils"
import { TableGroupedIds, NestedHistory } from "client/components"

export interface CcRequirementProps {
    requirementId: number
    hash?: string
}
export interface CcRequirementObjectProps {
    requirement: CcRequirement
    editAccess?: boolean
}
export interface CcRequirementReviewProps {
    requirementId: number
    revisionId: number
}

export type RequirementList = { [requirementId: number]: CcRequirement }
export type RequirementValidation = { [key in keyof CcRequirement]?: string}

export type RequirementParentChildList = { [requirementId: number]: readonly number[] }
export type RequirementParentChildPdfExport = {
    [requirementId: number]: {
        parents: number[]
        children: number[]
    }
}

export type RequirementVersionList = { [versionId: number]: CcRequirementVersion }
export type RequirementVersions = {
    [requirementId: number]: {
        versionIds: readonly number[]
        versionList: RequirementVersionList
    }
}

export type RequirementLifecyclePhaseList = { [lifecycleId: number]: readonly LifecyclePhase[] }

export type RequirementRevisionList = { [requirementId: number]: readonly CcRequirementRevision[] }
export type RequirementRevisionActiveList = { [requirementId: number]: CcRequirementRevision }
export type RequirementRevisionValidation = { [key in keyof CcRequirementRevision]?: string }

export type RequirementStateList = { [requirementId: number]: CcRequirementState }

export type RequirementHistoryList = { [requirementId: number]: CcRequirementHistory[] }
export type RequirementHistoryNestedList = { [requirementId: number]: NestedHistory[] }
export type RequirementHistoryValidation = { [key in keyof CcRequirementHistory]?: string }

export type RequirementTempAnswerByUser = { [userId: number]: CcRequirementTempAnswer }
export type RequirementTempAnswerList = { [requirementId: number]: RequirementTempAnswerByUser }
export type RequirementTempAnswerValidation = { [key in keyof CcRequirementTempAnswer]?: string }

export type RequirementTempAnswerVersionByUser = { [userId: number]: CcRequirementTempAnswerVersion[] }

export type RequirementsOnSelect = (requirements: readonly CcRequirement[]) => void

export type ReviewMethodsType = { [methodId: number]: boolean }
export type SmartCompliancesType = { [complianceId: number]: boolean }

export interface RequirementFilters {
    uniqueId?: string
    name?: string
    creator?: number

    typeId?: number
    priorityId?: number
    statusId?: number
    reviewMethods?: ReviewMethodsType
    smartCompliances?: SmartCompliancesType

    search?: string
}

export interface CcRequirementStoreState {
    // assign requirements to lifecycle phase
    selected: readonly number[]
    onSelect: RequirementsOnSelect

    // list
    baseRoute: string
    baseReviewRoute: string
    tableClass: string
    showColumnSettings: boolean
    dictionariesLoaded: boolean

    requirementList: RequirementList
    requirementParentChild: readonly CcRequirementParentChild[]
    requirementGroupedIds: readonly TableGroupedIds[]
    requirementIds: readonly number[]

    revisionList: RequirementRevisionList
    revisionActiveList: RequirementRevisionActiveList

    requirementStateList: RequirementStateList
    requirementHistoryList: RequirementHistoryList
    tempAnswerList: RequirementTempAnswerList

    sortColumn: keyof CcRequirement
    sortOrder: OrderType
    filters: RequirementFilters

    // edit
    requirementEdit: CcRequirement
    lifecyclesEdit: readonly Lifecycle[]
    lifecyclePhaseListEdit: RequirementLifecyclePhaseList

    // don't use "reloadList" - these data are reloaded after editing
    // don't use "reloadView" - this will coas reloading of all nested modals of View pages
    reloadEdit: boolean

    assigned: {
        parents: RequirementParentChildList
        children: RequirementParentChildList
    }
    notAssigned: { // both for parents and children
        requirementIds: readonly number[]
        sortColumn: keyof CcRequirement
        sortOrder: OrderType
        filters: RequirementFilters
        selected: readonly number[]
    }

    versionList: RequirementVersions

    // review
    selectedRequirementIds: readonly number[]
    visibleRequirementIds: readonly number[]

    reviewListLoadState: StateType

    // popup menu
    active: CcRequirement
    menuShow: boolean
    menuTarget: any

    // view history
    requirementDetails: CcRequirement
    requirementState: CcRequirementState
    requirementHistory: CcRequirementHistory[]
    tempAnswers: RequirementTempAnswerByUser
    tempAnswerVersions: RequirementTempAnswerVersionByUser
    tempAnswerHistory: CcRequirementHistory
    reloadAnswer: boolean
}

// assign requirements to lifecycle phase
export const CC_REQUIREMENT_SELECTED = "CC_REQUIREMENT_SELECTED"
export const CC_REQUIREMENT_ON_SELECT = "CC_REQUIREMENT_ON_SELECT"

// list
export const CC_REQUIREMENT_BASE_ROUTE = "CC_REQUIREMENT_BASE_ROUTE"
export const CC_REQUIREMENT_BASE_REVIEW_ROUTE = "CC_REQUIREMENT_BASE_REVIEW_ROUTE"
export const CC_REQUIREMENT_COLUMN_SETTINGS = "CC_REQUIREMENT_COLUMN_SETTINGS"
export const CC_REQUIREMENT_DICTIONARIES_LOADED = "CC_REQUIREMENT_DICTIONARIES_LOADED"

export const CC_REQUIREMENT_LIST = "CC_REQUIREMENT_LIST"
export const CC_REQUIREMENT_PARENT_CHILD = "CC_REQUIREMENT_PARENT_CHILD"
export const CC_REQUIREMENT_NESTED = "CC_REQUIREMENT_NESTED"
export const CC_REQUIREMENT_IDS = "CC_REQUIREMENT_IDS"

export const CC_REQUIREMENT_REVISION_LIST = "CC_REQUIREMENT_REVISION_LIST"
export const CC_REQUIREMENT_REVISION_ACTIVE_LIST = "CC_REQUIREMENT_REVISION_ACTIVE_LIST"

export const CC_REQUIREMENT_STATE_LIST = "CC_REQUIREMENT_STATE_LIST"
export const CC_REQUIREMENT_HISTORY_LIST = "CC_REQUIREMENT_HISTORY_LIST"
export const CC_REQUIREMENT_TEMP_ANSWER_LIST = "CC_REQUIREMENT_TEMP_ANSWER_LIST"

export const CC_REQUIREMENT_SORT = "CC_REQUIREMENT_SORT"
export const CC_REQUIREMENT_FILTERS = "CC_REQUIREMENT_FILTERS"

// edit
export const CC_REQUIREMENT_EDIT = "CC_REQUIREMENT_EDIT"
export const CC_REQUIREMENT_LIFECYCLES_EDIT = "CC_REQUIREMENT_LIFECYCLES_EDIT"
export const CC_REQUIREMENT_LIFECYCLE_PHASE_LIST_EDIT = "CC_REQUIREMENT_LIFECYCLE_PHASE_LIST_EDIT"

export const CC_REQUIREMENT_RELOAD_EDIT = "CC_REQUIREMENT_RELOAD_EDIT"

export const CC_REQUIREMENT_ASSIGNED_PARENTS = "CC_REQUIREMENT_ASSIGNED_PARENTS"
export const CC_REQUIREMENT_ASSIGNED_CHILDREN = "CC_REQUIREMENT_ASSIGNED_CHILDREN"

export const CC_REQUIREMENT_NOT_ASSIGNED_IDS = "CC_REQUIREMENT_NOT_ASSIGNED_IDS"
export const CC_REQUIREMENT_NOT_ASSIGNED_SORT = "CC_REQUIREMENT_NOT_ASSIGNED_SORT"
export const CC_REQUIREMENT_NOT_ASSIGNED_FILTERS = "CC_REQUIREMENT_NOT_ASSIGNED_FILTERS"
export const CC_REQUIREMENT_NOT_ASSIGNED_SELECTED = "CC_REQUIREMENT_NOT_ASSIGNED_SELECTED"

export const CC_REQUIREMENT_VERSION_LIST = "CC_REQUIREMENT_VERSION_LIST"

// review
export const CC_REQUIREMENT_SELECTED_REQUIREMENT_IDS = "CC_REQUIREMENT_SELECTED_REQUIREMENT_IDS"
export const CC_REQUIREMENT_VISIBLE_REQUIREMENT_IDS = "CC_REQUIREMENT_VISIBLE_REQUIREMENT_IDS"

export const CC_REQUIREMENT_REVIEW_LIST_LOAD_STATE = "CC_REQUIREMENT_REVIEW_LIST_LOAD_STATE"

// popup menu
export const CC_REQUIREMENT_ACTIVE = "CC_REQUIREMENT_ACTIVE"
export const CC_REQUIREMENT_POPUP_MENU_SHOW = "CC_REQUIREMENT_POPUP_MENU_SHOW"
export const CC_REQUIREMENT_POPUP_MENU_TARGET = "CC_REQUIREMENT_POPUP_MENU_TARGET"

// view history
export const CC_REQUIREMENT_REQUIREMENT_DETAILS = "CC_REQUIREMENT_REQUIREMENT_DETAILS"
export const CC_REQUIREMENT_REQUIREMENT_STATE = "CC_REQUIREMENT_REQUIREMENT_STATE"
export const CC_REQUIREMENT_REQUIREMENT_HISTORY = "CC_REQUIREMENT_REQUIREMENT_HISTORY"
export const CC_REQUIREMENT_TEMP_ANSWERS = "CC_REQUIREMENT_TEMP_ANSWERS"
export const CC_REQUIREMENT_TEMP_ANSWER_VERSIONS = "CC_REQUIREMENT_TEMP_ANSWER_VERSIONS"
export const CC_REQUIREMENT_TEMP_ANSWER_HISTORY = "CC_REQUIREMENT_TEMP_ANSWER_HISTORY"
export const CC_REQUIREMENT_RELOAD_ANSWER = "CC_REQUIREMENT_RELOAD_ANSWER"
